/**
 * WASSER E-commerce Platform - Design Tokens
 *
 * Унифицированные токены дизайна для Client и Admin приложений.
 * Эти значения используются в MUI theme и компонентах.
 *
 * @example
 * import { tokens } from '@shared/theme/tokens';
 *
 * // В стилях
 * padding: tokens.spacing.md  // 16px
 * borderRadius: tokens.borderRadius.md  // 12
 */

/**
 * Spacing Scale (8px base)
 * Используется для отступов, margins, paddings
 */
export const spacing = {
  /** 4px - минимальный отступ */
  xs: 4,
  /** 8px - маленький отступ */
  sm: 8,
  /** 16px - стандартный отступ */
  md: 16,
  /** 24px - большой отступ */
  lg: 24,
  /** 32px - очень большой отступ */
  xl: 32,
  /** 48px - максимальный отступ */
  xxl: 48,
};

/**
 * Border Radius Scale
 * Унифицировано: 12px как стандарт
 */
export const borderRadius = {
  /** 4px - для мелких элементов (badges) */
  xs: 4,
  /** 8px - для чипов */
  sm: 8,
  /** 10px - для кнопок small */
  button: 10,
  /** 12px - стандарт (карточки, кнопки, модалы) */
  md: 12,
  /** 16px - для крупных карточек */
  lg: 16,
  /** 24px - для hero секций */
  xl: 24,
  /** 50% - полный круг */
  round: '50%',
  /** 9999px - pill shape */
  pill: 9999,
};

/**
 * Shadow Scale
 * Разные уровни глубины для карточек и модалов
 */
export const shadows = {
  /** Без тени */
  none: 'none',
  /** Легкая тень - для карточек */
  sm: '0 2px 8px rgba(0, 0, 0, 0.08)',
  /** Средняя тень - для hover состояния */
  md: '0 4px 16px rgba(0, 0, 0, 0.12)',
  /** Сильная тень - для модалов */
  lg: '0 8px 32px rgba(0, 0, 0, 0.16)',
  /** Максимальная тень - для dropdown меню */
  xl: '0 12px 48px rgba(0, 0, 0, 0.20)',
  /** Hover состояние карточки */
  cardHover: '0 4px 20px rgba(0, 0, 0, 0.12)',
  /** Active/pressed состояние */
  cardActive: '0 1px 8px rgba(0, 0, 0, 0.1)',
};

/**
 * Transition Timings
 * Единые тайминги для анимаций
 */
export const transitions = {
  /** Быстрые анимации (hover, active) */
  fast: '150ms ease',
  /** Стандартные анимации */
  normal: '200ms ease',
  /** Медленные анимации (модалы, drawers) */
  slow: '300ms ease',
  /** Очень медленные (page transitions) */
  slower: '400ms ease',
  /** Easing функции */
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
};

/**
 * Z-Index Scale
 * Управление слоями UI
 */
export const zIndex = {
  /** Фоновые элементы */
  background: -1,
  /** Обычный контент */
  content: 0,
  /** Sticky элементы */
  sticky: 100,
  /** Dropdown меню */
  dropdown: 200,
  /** Модалы */
  modal: 1300,
  /** Снэкбары/тосты */
  snackbar: 1400,
  /** Тултипы */
  tooltip: 1500,
};

/**
 * Typography Scale
 * Font sizes и line heights
 */
export const typography = {
  fontSize: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    md: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    xxl: '1.5rem', // 24px
    h1: '2.5rem', // 40px
    h2: '2rem', // 32px
    h3: '1.75rem', // 28px
    h4: '1.5rem', // 24px
    h5: '1.25rem', // 20px
    h6: '1.1rem', // 17.6px
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.6,
    loose: 1.75,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  letterSpacing: {
    tight: '-0.02em',
    normal: '-0.01em',
    wide: '0.01em',
  },
};

/**
 * Touch Target Sizes
 * Apple/Google рекомендации для touch устройств
 */
export const touchTargets = {
  /** Минимальный размер (44x44px - Apple HIG) */
  min: 44,
  /** Стандартный размер кнопки */
  button: 44,
  /** Маленькая кнопка (36px min для вторичных действий) */
  small: 36,
  /** Большая кнопка */
  large: 52,
  /** Иконка в тулбаре */
  icon: 44,
};

/**
 * Breakpoints (соответствуют MUI)
 */
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

/**
 * Mobile-First Media Query Helpers
 * Используй min-width вместо max-width!
 *
 * @example
 * // В MUI sx prop:
 * sx={{
 *   padding: 1,  // Mobile base
 *   [mq.sm]: { padding: 2 },  // Tablet+
 *   [mq.md]: { padding: 3 },  // Desktop+
 * }}
 */
export const mq = {
  // Mobile-first: min-width queries
  sm: '@media (min-width: 600px)', // Tablet+
  md: '@media (min-width: 900px)', // Desktop+
  lg: '@media (min-width: 1200px)', // Large desktop+
  xl: '@media (min-width: 1536px)', // XL desktop+

  // Special queries
  touch: '@media (hover: none) and (pointer: coarse)',
  hover: '@media (hover: hover) and (pointer: fine)',
  landscape: '@media (orientation: landscape)',
  portrait: '@media (orientation: portrait)',
  reducedMotion: '@media (prefers-reduced-motion: reduce)',
  darkMode: '@media (prefers-color-scheme: dark)',
  highContrast: '@media (prefers-contrast: high)',
};

/**
 * Mobile-First Responsive Style Generator
 * Создаёт объект стилей с правильными media queries
 *
 * @example
 * const styles = responsive(
 *   { padding: 8 },   // mobile
 *   { padding: 16 },  // tablet
 *   { padding: 24 },  // desktop
 *   { padding: 32 }   // large desktop (optional)
 * );
 */
export const responsive = (mobile, tablet, desktop, largeDesktop) => ({
  // Base (mobile)
  ...mobile,
  // Tablet+
  [mq.sm]: tablet,
  // Desktop+
  [mq.md]: desktop,
  // Large desktop+ (optional)
  ...(largeDesktop ? { [mq.lg]: largeDesktop } : {}),
});

/**
 * Responsive Value Helper
 * Для простых значений вместо объектов
 *
 * @example
 * sx={{ padding: rv(1, 2, 3) }}
 * // Развернётся в: { padding: 1, [mq.sm]: { padding: 2 }, [mq.md]: { padding: 3 } }
 */
export const rv = (mobile, tablet, desktop, largeDesktop) => ({
  base: mobile,
  sm: tablet,
  md: desktop,
  lg: largeDesktop,
});

/**
 * Container Widths
 */
export const containerWidths = {
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

/**
 * Responsive Padding
 * Отступы для Container на разных экранах
 */
export const responsivePadding = {
  /** Desktop (lg+) */
  desktop: 24,
  /** Tablet (md) */
  tablet: 16,
  /** Mobile (sm) */
  mobile: 12,
  /** Very small mobile (<400px) */
  compact: 8,
};

/**
 * Animation Variants (для framer-motion)
 */
export const animations = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  slideUp: {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0 },
  },
  slideDown: {
    initial: { y: -20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: 20, opacity: 0 },
  },
  scale: {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.95, opacity: 0 },
  },
  // Для карточек при hover
  cardHover: {
    rest: { y: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
    hover: { y: -2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
  },
  // Для кнопок при нажатии
  buttonTap: {
    scale: 0.98,
    transition: { duration: 0.1 },
  },
};

export const tokens = {
  spacing,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  typography,
  touchTargets,
  breakpoints,
  mq,
  responsive,
  rv,
  containerWidths,
  responsivePadding,
  animations,
};

export default tokens;
